/**
 * Orchestrator for API Results Loading
 * Includes in-memory result cache (lives until page reload).
 * Cache key = searchTerm + searchType + lang + hiddenDicts + hiddenLangs.
 * # ⛓️ CURSOR_ANCHOR:search:results-api-loader
 */

(function() {
  'use strict';

  var stateHelper = window.SEARCH_RESULTS_API_LOADER_STATE;
  var utils = window.SEARCH_RESULTS_API_LOADER_UTILS;
  var morphing = window.SEARCH_RESULTS_API_LOADER_MORPHING;

  // ---- In-memory result cache ----
  var _cache = {};
  var _CACHE_MAX = 100; // max entries to prevent unbounded growth

  function _makeCacheKey(params) {
    // Deterministic key from search params
    return JSON.stringify({
      s: (params.search || '').trim().toUpperCase(),
      t: params.type || 'smart',
      l: params.lang || 'en',
      hd: (params.hide_dicts || []).slice().sort().join(','),
      hl: (params.hide_langs || []).slice().sort().join(',')
    });
  }

  function _cacheGet(key) {
    var entry = _cache[key];
    if (!entry) return null;
    return entry;
  }

  function _cachePut(key, data) {
    // Evict oldest if over limit
    var keys = Object.keys(_cache);
    if (keys.length >= _CACHE_MAX) {
      delete _cache[keys[0]];
    }
    _cache[key] = data;
  }

  /**
   * Функция для загрузки результатов через API с morphing-эффектом
   */
  function loadSearchResults(searchTerm, searchType) {
    var deps = stateHelper.getDeps();
    var state = stateHelper.getState();
    var fragmentBuilder = stateHelper.getFragmentBuilder();
    var resultsCount = stateHelper.getResultsCount();
    
    var getCurrentLang = deps.getCurrentLang || function() { return 'en'; };
    var getNothingFoundText = deps.getNothingFoundText || function() { return 'Nothing found'; };
    var cancelActiveRequest = state.cancelActiveRequest || function() {};
    var getNextSearchId = state.getNextSearchId || function() { return 0; };
    var setActiveRequest = state.setActiveRequest || function() {};
    var clearActiveRequest = state.clearActiveRequest || function() {};
    var isRequestCurrent = state.isRequestCurrent || function() { return false; };
    var createResultsFragment = fragmentBuilder.createResultsFragment || function() { return document.createDocumentFragment(); };
    var showResultsCount = resultsCount.showResultsCount || function() {};
    var initAccordion = deps.initAccordion || function() {};
    var initDictNameHandlers = deps.initDictNameHandlers || function() {};
    
    var currentLang = getCurrentLang();
    var nothingFoundText = getNothingFoundText();
    var container = document.getElementById('results-container');
    
    // Если запрос пустой, загружаем intro контент вместо вызова API
    if (!searchTerm || searchTerm.trim() === '') {
      var searchButton = document.querySelector('.search-button');
      if (searchButton) {
        searchButton.classList.remove('loading');
      }
      
      // Загружаем intro контент для текущего языка
      if (window.SEARCH_LANGUAGE && window.SEARCH_LANGUAGE.loadIntroContent) {
        window.SEARCH_LANGUAGE.loadIntroContent(currentLang);
      } else if (window.loadIntroContent) {
        window.loadIntroContent(currentLang);
      }
      return;
    }
    
    if (utils.checkEasterEgg(searchTerm, currentLang, container)) {
      return;
    }
    
    var searchButton = document.querySelector('.search-button');
    cancelActiveRequest();
    if (searchButton) {
      searchButton.classList.remove('loading');
    }
    
    if (searchButton) {
      searchButton.classList.add('loading');
    }
    
    var thisSearchId = getNextSearchId();
    
    // Плавное исчезновение старых результатов при начале нового поиска.
    // При навигации к конкретному термину (история / hash) анимация пропускается:
    // её translateY(20px) сдвигал getBoundingClientRect цели, и performScrollToElement
    // прокручивал слишком высоко — заголовок статьи уезжал под sticky-панели.
    var _skipContainerFade = !!(window.PENDING_TERM_GUID || window.PENDING_TERM_INDEX || window.location.hash);
    if (container) {
      if (_skipContainerFade) {
        // Гарантируем, что никакой fade-in не остался от предыдущего поиска —
        // иначе transform translateY сохранится и собьёт замеры rect при скролле.
        container.classList.remove('results-fade-in');
        container.classList.add('results-visible');
      } else {
        container.classList.add('results-fade-in');
        container.classList.remove('results-visible');
      }
    }
    
    utils.cleanupOldResults(container);
    var searchParams = utils.buildSearchParams(searchTerm, searchType, currentLang);

    // ---- Cache check: serve from memory if same query was already fetched ----
    var cacheKey = _makeCacheKey(searchParams);
    var cached = _cacheGet(cacheKey);
    if (cached) {
      console.log('[SEARCH] Cache hit for:', searchTerm);
      if (searchButton) searchButton.classList.remove('loading');

      // Reuse the cached data — go straight to rendering
      var resultsCountCached = cached.success && cached.results && cached.results.count ? cached.results.count : 0;
      if (resultsCountCached > 5) {
        showResultsCount(resultsCountCached);
      }
      morphing.handleResponseWithoutMorphing(cached, container, nothingFoundText, searchTerm, currentLang, searchType, thisSearchId, state);
      return;
    }
    // ---- End cache check ----

    var abortController = new AbortController();
    setActiveRequest(abortController);

    fetch('/api', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(searchParams),
      signal: abortController.signal
    })
      .then(response => {
        if (!isRequestCurrent(thisSearchId)) {
          console.log('[SEARCH] Игнорируем устаревший ответ запроса');
          return null;
        }
        if (!response.ok) {
          if (response.status === 429) {
            // Превышение лимита - показываем капчу
            if (window.CAPTCHA_HANDLER) {
              window.CAPTCHA_HANDLER.showCaptcha(function() {
                // После успешного решения повторяем поиск
                loadSearchResults(searchTerm, searchType);
              });
              // Возвращаем специальный промис, который не пойдет в .then(data)
              return new Promise(function() {}); 
            }
          }
          throw new Error('HTTP error! status: ' + response.status);
        }
        return response.text().then(function(text) {
          if (!text || text.trim() === '') {
            throw new Error('Empty response from server');
          }
          try {
            return JSON.parse(text);
          } catch (e) {
            console.error('[SEARCH] JSON parse error:', e);
            throw new Error('Invalid JSON response: ' + e.message);
          }
        });
      })
      .then(data => {
        if (!isRequestCurrent(thisSearchId) || !data) {
          console.log('[SEARCH] Игнорируем устаревшие данные');
          return;
        }
        
        clearActiveRequest();

        // Store successful response in cache
        if (data && data.success) {
          _cachePut(cacheKey, data);
        }

        // Если API вернул intro контент (пустой search, но есть lang)
        if (data.success && data.intro) {
          clearActiveRequest();
          
          if (container) {
            container.classList.add('results-fade-in');
            container.classList.remove('results-visible');
          }
          
          setTimeout(function() {
            if (container) {
              container.innerHTML = data.intro;
              if (window.SLOVARY_REF_COLLAPSED && window.SLOVARY_REF_COLLAPSED.init) {
                window.SLOVARY_REF_COLLAPSED.init(container);
              }
              
              requestAnimationFrame(function() {
                container.classList.add('results-visible');
                container.classList.remove('results-fade-in');
              });
            }
          }, 50);
          
          if (searchButton) {
            searchButton.classList.remove('loading');
          }
          return;
        }
        
        // Обработка ошибок от API
        if (!data.success && data.error) {
          var errorMessage = data.message || data.error || 'Ошибка поиска';
          
          // Если это ошибка валидации для поиска по описаниям, используем i18n
          if (data.error === 'description_search_min_length') {
            if (typeof LANG_TEXTS !== 'undefined' && window.SEARCH_LANGUAGE) {
              var currentLang = window.SEARCH_LANGUAGE.getCurrentLang();
              if (LANG_TEXTS[currentLang] && LANG_TEXTS[currentLang].description_search_min_length) {
                errorMessage = LANG_TEXTS[currentLang].description_search_min_length;
              }
            }
          }
          
          if (container) {
            container.classList.add('results-fade-in');
            container.classList.remove('results-visible');
          }
          
          var errorHtml = '<p data-search-result="true" style="color: #d32f2f; padding: 20px; background: #ffebee; border-radius: 4px; margin: 20px 0;">' + errorMessage + '</p>';
          
          setTimeout(function() {
            if (container) {
              container.innerHTML = errorHtml;
              requestAnimationFrame(function() {
                container.classList.add('results-visible');
                container.classList.remove('results-fade-in');
              });
            }
          }, 50);
          
          if (searchButton) {
            searchButton.classList.remove('loading');
          }
          return;
        }
        
        var newEntries = data.success && data.results.count > 0 ? data.results.entries : null;
        var message = data.success && data.results.message ? data.results.message : null;
        var resultsCountValue = data.success && data.results.count ? data.results.count : 0;
        
        if (searchButton) {
          if (resultsCountValue > 5) {
            // Если будем показывать счетчик, убираем loading одновременно с show-count
            // чтобы избежать мерцания иконки
            searchButton.classList.remove('loading');
            showResultsCount(resultsCountValue);
          } else {
            // Если счетчик не показываем, просто убираем loading
            searchButton.classList.remove('loading');
          }
        }
        
        var currentExistingResults = container.querySelectorAll('.opredelenie');
        
        // Убираем эффект морфинга - всегда вставляем результаты напрямую
        morphing.handleResponseWithoutMorphing(data, container, nothingFoundText, searchTerm, currentLang, searchType, thisSearchId, state);
      })
      .catch(error => {
        if (searchButton) {
          searchButton.classList.remove('loading');
        }
        if (error.name === 'AbortError') {
          console.log('[SEARCH] Запрос отменен');
          return;
        }
        if (!isRequestCurrent(thisSearchId)) {
          console.log('[SEARCH] Игнорируем ошибку устаревшего запроса');
          return;
        }
        clearActiveRequest();
        console.error('[SEARCH] Ошибка загрузки результатов:', error);
        
        var errorMessage = 'Ошибка загрузки результатов';
        if (error.message) {
          if (error.message.includes('JSON')) {
            errorMessage = 'Ошибка обработки ответа сервера. Попробуйте еще раз.';
          } else if (error.message.includes('HTTP error')) {
            errorMessage = 'Ошибка соединения с сервером. Проверьте подключение к интернету.';
          } else {
            errorMessage = 'Ошибка: ' + error.message;
          }
        }
        
        var errorHtml = '<p data-search-result="true" style="color: #d32f2f; padding: 20px; background: #ffebee; border-radius: 4px; margin: 20px 0;">' + errorMessage + '</p>';
        container.appendChild(document.createRange().createContextualFragment(errorHtml));
      });
  }

  window.SEARCH_RESULTS_API_LOADER = {
    loadSearchResults: loadSearchResults
  };
})();

